import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, Injectable, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export interface Notification {
  id: number;
  kind: 'error' | 'info';
  message: string;
}

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 3000;

@Injectable({ providedIn: 'root' })
export class MessageService {

  private readonly translateService = inject(TranslateService);
  private nextId = 1;
  private readonly _messages = signal<Notification[]>([]);
  readonly messages = this._messages.asReadonly();

  notifyError(message: string): void {
    this.pushAutoDismissed({ id: this.nextId++, kind: 'error', message });
  }

  notifyInfo(message: string): void {
    this.pushAutoDismissed({ id: this.nextId++, kind: 'info', message });
  }

  dismiss(id: number): void {
    this._messages.update((list) => list.filter((n) => n.id !== id));
  }

  handleBackendError(err: HttpErrorResponse): void {
    this.notifyError(this.localize(err));
  }

  // Localised, code-aware message for a backend error — never the raw (often English) server text.
  // The error code always wins over the HTTP status, so a mapped code is used even on a 500.
  localize(err: HttpErrorResponse): string {
    const e = this.translateService.instant('backendErrors') as Record<string, string>;
    const code = this.codeOf(err);
    if (code && code in e) return e[code];
    if (err.status === 0) return e['__networkDown'];
    if (err.status >= 500) return e['__serverError'];
    if (err.status >= 400) return e['__badRequest'];
    return e['__default'];
  }

  // The backend error code, read whether the body arrived parsed (the usual case) or as a raw JSON
  // string (which would otherwise make `.code` undefined and lose the code-based mapping).
  codeOf(err: HttpErrorResponse): string | undefined {
    const body = err.error;
    if (body && typeof body === 'object') return (body as { code?: string }).code;
    if (typeof body === 'string') {
      try {
        return (JSON.parse(body) as { code?: string })?.code;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  private pushAutoDismissed(n: Notification): void {
    this._messages.update((list) => {
      const next = [...list, n];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    setTimeout(() => this.dismiss(n.id), AUTO_DISMISS_MS);
  }

}

@Component({
  selector: 'app-toast',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './toast.css',
  template: `
    <div class="toast" aria-live="polite" aria-atomic="false">
      @for (n of items(); track n.id) {
        <div
          class="toast-item"
          [class.toast-item-error]="n.kind === 'error'"
          [class.toast-item-info]="n.kind === 'info'"
          role="status"
        >
          <div class="toast-bar"></div>
          <div class="toast-body">{{ n.message }}</div>
          <button
            type="button"
            class="toast-close"
            (click)="messageService.dismiss(n.id)"
            [attr.aria-label]="'notifications.dismissAria' | translate"
          >
            {{ 'notifications.dismissSymbol' | translate }}
          </button>
        </div>
      }
    </div>
  `,
})
export class Toast {

  protected readonly messageService = inject(MessageService);

  protected readonly items = this.messageService.messages;

}
